/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babbnov
 * @version 1.22.0
 * @since 1.15.0
 */

import compact from 'lodash/compact';
import invert from 'lodash/invert';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { requestSaga } from '../../sagas/requestSaga';
import { stringFormatParams } from '../../utils/stringFormat';
import {
    API_ONBOARDING_COMPLETE,
    API_ONBOARDING_DISABLE,
    API_ONBOARDING_DISABLE_ALL,
    API_ONBOARDING_ENABLE,
    API_ONBOARDING_GET_MODAL,
    API_ONBOARDING_GET_STATUS,
    API_ONBOARDING_SET_ELEMENT_VIEWED,
    API_ONBOARDING_SET_MODAL_VIEWED,
    API_ONBOARDING_SHOW_LATER,
} from '../app/Api';
import { modalCloseAll } from '../modal/modalActions';
import { AccessComponentKeyEnum } from '../role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../role/selectors/guardSelector';
import {
    onboardingFinishAction,
    onboardingInitSuccessAction,
    onboardingPageReadyForStartAction,
} from './onboardingActions';
import {
    ONBOARDING_DISABLE,
    ONBOARDING_DISABLE_ALL,
    ONBOARDING_ENABLE,
    ONBOARDING_FINISH,
    ONBOARDING_INIT_START,
    ONBOARDING_MODAL_VIEWED,
    ONBOARDING_NEXT_STEP,
    ONBOARDING_SHOW_LATER,
} from './onboardingConstants';
import {
    IOnboardingModalResponse,
    IOnboardingStatusResponse,
    mapOnboardingElementId,
    mapOnboardingModalId,
    mapOnboardingPages,
} from './onboardingMapping';
import { makeSelectOnboardingIsDisplayedByUser, makeSelectOnboardingPage } from './onboardingSelectors';
import {
    TOnboardingFinishAction,
    TOnboardingModalViewedAction,
    TOnboardingNextStepAction,
} from './types/onboardingActionTypes';
import { EOnboardingModalId, EOnboardingPage } from './types/onboardingTypes';

export function* onboardingPageUrlSaga(url: string): SagaIterator {
    const page: EOnboardingPage = yield select(makeSelectOnboardingPage());
    const id = mapOnboardingPages[page];

    return stringFormatParams(url, { id });
}

export function* onboardingInitSaga(): SagaIterator {
    try {
        const isOnboardingFeatureEnabled =
            yield select(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.onboarding));
        const isDisplayedByUser = yield select(makeSelectOnboardingIsDisplayedByUser());
        if (!isOnboardingFeatureEnabled && !isDisplayedByUser) {
            yield put(onboardingFinishAction({ noSendRequest: true }));
            return;
        }
        const urlStatus = yield call(onboardingPageUrlSaga, API_ONBOARDING_GET_STATUS);
        const responseStatus: IOnboardingStatusResponse = yield call(requestSaga, urlStatus, 'GET');

        if (responseStatus.isOnboardingExists && !responseStatus.isDisabledByUser) {
            // Если есть элементы для показа, проверяем нужно ли показать модальное окно
            let modalStartId;
            if (responseStatus.unviewedElementsIds.length) {
                const responseModal: IOnboardingModalResponse = yield call(requestSaga, API_ONBOARDING_GET_MODAL, 'GET');
                modalStartId = mapOnboardingModalId[responseModal.modalId];
            }

            const modalFinalId = !responseStatus.isWelcomeOnboardingCompleted
                ? EOnboardingModalId.modalFinal
                : undefined;
            const stepsIds = compact(responseStatus.unviewedElementsIds
                .map(component => mapOnboardingElementId[component]));

            yield put(onboardingInitSuccessAction({
                isDisplayedByUser: true,
                isOnboardingExists: true,
                isWelcomeOnboardingCompleted: responseStatus.isWelcomeOnboardingCompleted,
                modalFinalId,
                modalStartId,
                stepsIds,
            }));
        } else {
            yield put(onboardingFinishAction({ noSendRequest: true }));
        }
    } catch (err) {
        console.error(err);
    }
}

export function* onboardingNextStepSaga({ payload }: TOnboardingNextStepAction): SagaIterator {
    try {
        if (payload.alreadySeen) {
            return;
        }

        const id = invert(mapOnboardingElementId)[payload.completeId];
        const url = stringFormatParams(API_ONBOARDING_SET_ELEMENT_VIEWED, { id });
        yield call(requestSaga, url, 'POST');
    } catch (err) {
        console.error(err);
    }
}

export function* onboardingCompleteSaga({ payload }: TOnboardingFinishAction): SagaIterator {
    try {
        if (payload?.noSendRequest) {
            return;
        }

        const url = yield call(onboardingPageUrlSaga, API_ONBOARDING_COMPLETE);
        yield call(requestSaga, url, 'POST');
    } catch (err) {
        console.error(err);
    }
}

export function* onboardingModalViewedSaga({ payload }: TOnboardingModalViewedAction): SagaIterator {
    try {
        const modalIds = [invert(mapOnboardingModalId)[payload.id]];
        yield call(requestSaga, API_ONBOARDING_SET_MODAL_VIEWED, 'POST', { modalIds });
    } catch (err) {
        console.error(err);
    }
}

export function* onboardingEnableSaga(): SagaIterator {
    try {
        yield put(modalCloseAll());
        const url = yield call(onboardingPageUrlSaga, API_ONBOARDING_ENABLE);
        yield call(requestSaga, url, 'POST');
        yield call(onboardingInitSaga);
        yield put(onboardingPageReadyForStartAction());
    } catch (err) {
        console.error(err);
    }
}


export function* onboardingDisableSaga(): SagaIterator {
    try {
        const url = yield call(onboardingPageUrlSaga, API_ONBOARDING_DISABLE);
        yield call(requestSaga, url, 'POST');
    } catch (err) {
        console.error(err);
    }
}

export function* onboardingDisableAllSaga(): SagaIterator {
    try {
        yield call(requestSaga, API_ONBOARDING_DISABLE_ALL, 'POST');
    } catch (err) {
        console.error(err);
    }
}

export function* onboardingShowLaterSaga(): SagaIterator {
    try {
        yield call(requestSaga, API_ONBOARDING_SHOW_LATER, 'POST');
    } catch (err) {
        console.error(err);
    }
}


export function* onboardingSagas(): SagaIterator {
    yield all([
        takeEvery(ONBOARDING_INIT_START, onboardingInitSaga),
        takeEvery(ONBOARDING_NEXT_STEP, onboardingNextStepSaga),
        takeEvery(ONBOARDING_MODAL_VIEWED, onboardingModalViewedSaga),
        takeEvery(ONBOARDING_FINISH, onboardingCompleteSaga),
        takeEvery(ONBOARDING_DISABLE_ALL, onboardingDisableAllSaga),
        takeEvery(ONBOARDING_ENABLE, onboardingEnableSaga),
        takeEvery(ONBOARDING_DISABLE, onboardingDisableSaga),
        takeEvery(ONBOARDING_SHOW_LATER, onboardingShowLaterSaga),
    ]);
}
